import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ClockCounterClockwise, ArrowCounterClockwise, Spinner } from "@phosphor-icons/react";
import { listVersions } from "@/lib/api";

export default function VersionHistoryDialog({ open, onOpenChange, projectId, email, canEdit, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const v = await listVersions(projectId, email);
        setVersions(v);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, projectId, email]);

  const handleRestore = async (id) => {
    setRestoring(id);
    await onRestore(id);
    setRestoring(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-2xl" data-testid="version-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <ClockCounterClockwise size={22} weight="bold" /> Version History
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Restore any saved snapshot. Distribution list & admin are preserved.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[480px] overflow-y-auto custom-scrollbar border border-slate-200 rounded-md bg-white divide-y divide-slate-200">
          {loading && (
            <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Spinner size={14} weight="bold" className="animate-spin" />
              Loading history…
            </div>
          )}
          {!loading && versions.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="empty-versions">
              No saved versions yet. Click <span className="font-semibold">Save Version</span> to create one.
            </div>
          )}
          {!loading && versions.map((v) => (
            <div key={v.id} className="p-4 flex items-start justify-between gap-4" data-testid={`version-${v.id}`}>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500">
                  {new Date(v.created_at).toLocaleString()}
                </div>
                <div className="text-sm font-medium text-slate-950 mt-0.5 truncate">
                  {v.note || "Untitled version"}
                </div>
                <div className="text-xs text-slate-500 mt-1 font-mono-ibm truncate">
                  by {v.author_email || "unknown"}
                </div>
              </div>
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={restoring === v.id}
                  onClick={() => handleRestore(v.id)}
                  className="rounded-md border-slate-300 shrink-0"
                  data-testid={`restore-${v.id}`}
                >
                  <ArrowCounterClockwise size={12} weight="bold" />
                  <span className="ml-2">{restoring === v.id ? "Restoring…" : "Restore"}</span>
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
